import React, { useState, forwardRef, useEffect } from 'react'
import { FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

interface MyInputProps {
  value: string
  autoComplete?: string
  label: string
  error?: string
  onChange: (value: string) => void
  type?: string
  className?: string
  icon?: React.ReactNode
  min?: number
  max?: number
  isInvalid?: boolean
  required?: boolean
  placeholder?: string
  disabled?: boolean
  onFocus?: () => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  inputRef?: React.RefObject<HTMLInputElement>
  showClearButton?: boolean
  onClear?: () => void
  maxLength?: number
  helperText?: string
  suffix?: React.ReactNode
}

const MyInput = forwardRef<HTMLInputElement, MyInputProps>(
  (
    {
      value,
      autoComplete,
      label,
      error,
      suffix,
      onChange,
      maxLength,
      type = 'text',
      className = '',
      icon,
      min,
      max,
      isInvalid = false,
      required = false,
      placeholder = '',
      onFocus,
      onBlur,
      onKeyDown,
      inputRef,
      showClearButton = true,
      onClear,
      helperText,
      disabled,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [isFocused, setIsFocused] = useState(false)
    const [internalValue, setInternalValue] = useState(value)
    const [isPasswordVisible, setIsPasswordVisible] = useState(false)

    useEffect(() => {
      setInternalValue(value)
    }, [value])

    const handleFocus = () => {
      setIsFocused(true)
      if (onFocus) onFocus()
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      if (onBlur) onBlur(e)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      let limitedValue = newValue
      if (maxLength) {
        limitedValue = newValue.slice(0, maxLength)
      }

      setInternalValue(limitedValue)
      onChange(limitedValue)
    }

    const handleClear = () => {
      setInternalValue('')
      if (onClear) {
        onClear()
      } else {
        onChange('')
      }
    }

    const togglePasswordVisibility = () => {
      setIsPasswordVisible(prev => !prev)
    }

    const getBackgroundColor = () => {
      if (className.includes('!bg-white'))
        return 'bg-white hover:bg-white rounded-md'
      if (isInvalid || error) return 'bg-redlight hover:bg-redlight rounded-md'
      if (required && !internalValue) return 'bg-redlight rounded-md'
      return internalValue
        ? 'bg-grisclair hover:bg-grisclair2 rounded-md'
        : 'bg-grisclair hover:bg-grisclair2 rounded-md'
    }

    const labelClasses =
      isFocused || value
        ? 'absolute -translate-y-1/2 -translate-x-1/2 left-1/2 ml-0 w-auto font-semibold text-xs text-anthracite/60 bg-transparent rounded-t-md px-2 py-0 z-10'
        : `absolute inset-0 flex items-center ${
            icon ? 'pl-10' : 'pl-2'
          } text-gray-400 text-sm`

    return (
      <div
        className={`flex flex-col w-full rounded-md hover:rounded-md relative ${className}`}
      >
        <div
          className={`relative flex w-full h-full cursor-pointer transition-all rounded-md hover:rounded-md ${getBackgroundColor()} ${
            isInvalid ? 'border-redpink' : ''
          }`}
        >
          <label htmlFor={`input-${label}`} className={labelClasses}>
            {t(label)}
            {required && ' *'}
          </label>

          {icon && (
            <div className='absolute left-0 top-0 bottom-0 flex items-center px-3 text-anthracite bg-transparent rounded-l-md'>
              {icon}
            </div>
          )}

          <input
            id={`input-${label}`}
            ref={inputRef || ref}
            className={`w-full h-10 bg-transparent text-sm border-none outline-none ${
              icon ? 'pl-10' : 'px-2'
            } ${suffix ? 'pr-10' : 'pr-8'} ${getBackgroundColor()}`}
            value={internalValue}
            type={
              type === 'password' && !isPasswordVisible ? 'password' : 'text'
            }
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            min={min}
            max={max}
            maxLength={maxLength}
            autoComplete={autoComplete}
            required={required}
            placeholder={placeholder}
            disabled={disabled}
          />

          {(error || helperText) && (
            <span className='text-redpink text-xs absolute -bottom-4 mb-3 left-10'>
              {error ? t(error) : helperText}
            </span>
          )}
          {suffix && (
            <div className='absolute right-2 top-1/2 transform -translate-y-1/2'>
              {suffix}
            </div>
          )}
          {showClearButton && value && (
            <button
              type='button'
              className='absolute right-2 top-1/2 transform -translate-y-1/2'
              onClick={handleClear}
              tabIndex={-1}
              aria-label='Clear input'
            >
              <FaTimes
                role='img'
                className='text-anthracite hover:text-redpink'
              />
            </button>
          )}

          {type === 'password' && (
            <button
              type='button'
              className='absolute right-2 top-1/2 transform -translate-y-1/2'
              onClick={togglePasswordVisibility}
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
            >
              {isPasswordVisible ? (
                <FaEyeSlash className='text-anthracite hover:text-redpink' />
              ) : (
                <FaEye className='text-anthracite hover:text-redpink' />
              )}
            </button>
          )}
        </div>
      </div>
    )
  },
)

export default MyInput
